import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider, useTheme } from '@/lib/theme.jsx';
import Seo from '@/components/Seo';
import NavigationHeader from '@/components/NavigationHeader';
import Footer from '@/components/Footer';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Services from '@/pages/Services';
import Events from '@/pages/Events';
import Media from '@/pages/Media';
import Contact from '@/pages/Contact';
import Admin from '@/pages/Admin';
import '@/App.css';

const ROUTE_META = {
    '/': {
        title: 'Fractional PR and Strategy Agency | CommPro Worldwide',
        description: 'CommPro Worldwide is a fractional PR and strategy agency driving market adoption, executive visibility, and lead generation at scale since 2015.',
    },
    '/about': {
        title: 'About the Firm | CommPro Worldwide',
        description: 'Since 2015, CommPro Worldwide has helped organizations accelerate growth and strengthen stakeholder influence across borders, sectors, and institutions.',
    },
    '/services': {
        title: 'Fractional PR & Program Management Services | CommPro Worldwide',
        description: 'Fractional PR, market adoption strategy, public-private partnerships, executive visibility, event activation, and lead generation programs at scale.',
    },
    '/events': {
        title: 'Event Activations & Representation | CommPro Worldwide',
        description: 'CommPro Worldwide represents clients at the summits and forums where decisions are made — delegations, keynote placement, and on-site activation.',
    },
    '/media': {
        title: 'Media Relations & Press | CommPro Worldwide',
        description: 'Media relations practiced as counsel: editorial board access, broadcast placement, narrative engineering, and rapid response from CommPro Worldwide.',
    },
    '/contact': {
        title: 'Request a Consultation | CommPro Worldwide',
        description: 'Request a strategic consultation with CommPro Worldwide. Share your market adoption or corporate development objectives and a senior advisor responds.',
    },
    '/admin': {
        title: 'Admin | CommPro Worldwide',
        description: 'Restricted access.',
        noindex: true,
    },
};

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname]);
    return null;
};

const RouteMeta = () => {
    const { pathname } = useLocation();
    const meta = ROUTE_META[pathname] || ROUTE_META['/'];
    return <Seo title={meta.title} description={meta.description} path={pathname} noindex={meta.noindex} />;
};

const SiteLayout = () => (
    <div className="grain bg-ink min-h-screen">
        <NavigationHeader />
        <main>
            <Outlet />
        </main>
        <Footer />
    </div>
);

function AppShell() {
    const { theme } = useTheme();

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <RouteMeta />
            <Routes>
                <Route element={<SiteLayout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/media" element={<Media />} />
                    <Route path="/contact" element={<Contact />} />
                </Route>
                <Route path="/admin" element={<Admin />} />
            </Routes>
            <Toaster theme={theme} position="bottom-right" toastOptions={{
                style: {
                    background: 'rgb(var(--surface))',
                    border: '1px solid rgb(var(--line))',
                    color: 'rgb(var(--cream))',
                },
            }} />
        </BrowserRouter>
    );
}

function App() {
    return (
        <HelmetProvider>
            <ThemeProvider>
                <AppShell />
            </ThemeProvider>
        </HelmetProvider>
    );
}

export default App;
